/**
 * REST admin endpoints — DOC1 §11.1 + DOC4 AP-04/05/06/07.
 *
 *   POST /admin/firmware                       — upload firmware version
 *   POST /admin/firmware/{id}/rollout          — start staged OTA rollout
 *   GET  /admin/users  / POST /admin/users/{id}/block   — user management (DOC4 AP-05)
 *   GET  /admin/roles  / POST /admin/roles/assign       — role management (DOC4 AP-04)
 *   GET  /admin/warranty  / POST /admin/warranty/{id}   — warranty (DOC4 AP-04)
 *   POST /admin/reports                                  — reports (DOC4 AP-07)
 *
 * DOC4-narrative endpoints (users/roles/warranty/reports) are RATIFIED here per T018.
 */
import { z } from 'zod';
import { UuidSchema } from '../common/id.js';
import { E164MobileSchema } from '../common/mobile.js';
import { Iso8601Schema } from '../common/timestamp.js';
import { ControllerFamilySchema, DeviceTypeSchema } from './devices.js';
import { MemberAuthorityRoleSchema } from './members.js';
// ── POST /admin/members ────────────────────────────────────────────────────
export const AdminCreateMemberBody = z.object({
    parentUserId: UuidSchema,
    name: z.string().min(1).max(128),
    mobile: E164MobileSchema,
    authorityRole: MemberAuthorityRoleSchema,
});
// ── POST /admin/firmware ───────────────────────────────────────────────────
export const FirmwareUploadBody = z.object({
    controllerFamily: ControllerFamilySchema,
    /** Semantic version, e.g. "1.4.2". */
    version: z.string().regex(/^\d+\.\d+\.\d+(?:[-+].+)?$/),
    /** Which product types this image fits. */
    compatibleTypeIds: z.array(UuidSchema).min(1),
    /** Signed artifact URL in storage. */
    artifactUrl: z.string().url(),
    /** SHA-256 of the artifact. */
    checksum: z.string().min(1),
    /** Detached signature over the artifact. */
    signature: z.string().min(1),
});
export const FirmwareUploadResponse = z.object({
    firmwareVersionId: UuidSchema,
    isPublished: z.boolean(),
});
// ── POST /admin/firmware/{id}/rollout ──────────────────────────────────────
export const RolloutStateSchema = z.enum([
    'pending',
    'in_progress',
    'paused',
    'completed',
    'rolled_back',
]);
export const RolloutStartBody = z.object({
    /** Optional: roll out to N devices first (staged). */
    stageSize: z.number().int().min(1).optional(),
    /** Optional: restrict to specific devices. */
    deviceIds: z.array(UuidSchema).optional(),
});
export const RolloutStartResponse = z.object({
    rolloutId: UuidSchema,
    state: RolloutStateSchema,
});
// ── GET /admin/users  + POST /admin/users/{id}/block ───────────────────────
export const AdminAppRoleSchema = z.enum([
    'admin',
    'installer',
    'production_test',
    'main_user',
    'sub_main_user',
    'member',
    'sub_member',
]);
export const AdminUserSchema = z.object({
    id: UuidSchema,
    mobile: E164MobileSchema,
    language: z.string().min(2).max(8),
    status: z.enum(['active', 'blocked']),
    createdAt: Iso8601Schema,
    deviceCount: z.number().int().nonnegative(),
    name: z.string().nullable(),
    role: AdminAppRoleSchema,
    device: z.string().nullable(),
    since: Iso8601Schema,
});
export const AdminUserListQuerySchema = z.object({
    cursor: z.string().optional(),
    role: AdminAppRoleSchema.optional(),
    search: z.string().optional(),
});
export const AdminUserListResponse = z.object({
    users: z.array(AdminUserSchema),
    nextCursor: z.string().nullable(),
});
export const AdminUserBlockBody = z.object({
    blocked: z.boolean(),
    reason: z.string().max(500).optional(),
});
// ── GET /admin/roles  + POST /admin/roles/assign ───────────────────────────
//
// The `admin_roles` table (admin | staff) is the **admin-panel access**
// sub-table and remains as-is. The broader §3 role hierarchy lives in
// `app_roles` and is managed via /admin/app-roles/* below.
export const AdminRoleSchema = z.enum(['admin', 'staff']);
export const AdminRoleAssignmentSchema = z.object({
    userId: UuidSchema,
    role: AdminRoleSchema,
    assignedAt: Iso8601Schema,
});
export const AdminRoleListResponse = z.object({
    assignments: z.array(AdminRoleAssignmentSchema),
});
export const AdminRoleAssignBody = z.object({
    userId: UuidSchema,
    role: AdminRoleSchema,
});
// ── App-role hierarchy (§3) — Phase 1 ─────────────────────────────────────
//
//   GET    /admin/app-roles            list every (user, role) assignment
//   POST   /admin/app-roles/assign     assign a role by mobile number
//   DELETE /admin/app-roles/{userId}   revoke a role
//   POST   /admin/app-roles/grant      grant a specific permission to a sub-role
//   DELETE /admin/app-roles/grant      revoke a permission grant
//
// All five endpoints enforce the strict-below hierarchy rule (caller's role
// must be strictly above the target's role). Violations return 403 with
// code `ROLE_HIERARCHY_VIOLATION`.
import { AppRoleSchema, AppPermissionSchema } from '../auth/roles.js';
import { DeviceLifecycleStateSchema, DeviceLifecycleHistoryResponse } from './lifecycle.js';
export const AppRoleAssignmentSchema = z.object({
    userId: UuidSchema,
    mobile: E164MobileSchema,
    role: AppRoleSchema,
    parentUserId: UuidSchema.nullable(),
    assignedAt: Iso8601Schema,
    assignedById: UuidSchema.nullable(),
});
export const AppRoleListResponse = z.object({
    assignments: z.array(AppRoleAssignmentSchema),
});
export const AppRoleAssignBody = z.object({
    /** Target user identified by mobile (created on first login if absent). */
    mobile: E164MobileSchema,
    role: AppRoleSchema,
    /**
     * Optional display name to seed on the (possibly new) user — used when an
     * admin creates a named account, e.g. a production tester. The user can
     * still change it later.
     */
    displayName: z.string().min(1).max(80).optional(),
});
export const AppRoleGrantBody = z.object({
    granteeUserId: UuidSchema,
    permission: AppPermissionSchema,
});
// ── GET /admin/warranty  + POST /admin/warranty  + POST /admin/warranty/{id} ───
export const WarrantyClaimStateSchema = z.enum([
    'open',
    'in_review',
    'approved',
    'rejected',
    'closed',
]);
export const WarrantyClaimSchema = z.object({
    id: UuidSchema,
    deviceId: UuidSchema,
    ownerId: UuidSchema,
    state: WarrantyClaimStateSchema,
    description: z.string(),
    remark: z.string().nullable().optional(),
    createdAt: Iso8601Schema,
    updatedAt: Iso8601Schema,
});
export const WarrantyClaimListResponse = z.object({
    claims: z.array(WarrantyClaimSchema),
    nextCursor: z.string().nullable(),
});
export const WarrantyClaimUpdateBody = z.object({
    state: WarrantyClaimStateSchema,
    note: z.string().max(2000).optional(),
    remark: z.string().max(2000).optional(),
});
// POST /admin/warranty — admin files a new claim on behalf of the device owner.
// Owner is resolved server-side from devices.owner_id; the client only supplies
// the deviceId. New claims start in state 'open'.
export const WarrantyClaimCreateBody = z.object({
    deviceId: UuidSchema,
    description: z.string().max(2000).default(''),
});
export const WarrantyClaimCreateResponse = z.object({
    id: UuidSchema,
    deviceId: UuidSchema,
    ownerId: UuidSchema,
    state: z.literal('open'),
    createdAt: Iso8601Schema,
});
// ── GET /admin/warranty/devices — per-device warranty coverage ─────────────
// Mirrors the mobile app's Warranty screen: every registered device carries
// warranty_expires_at (stamped at claim/registration time); purchase date is
// derived by subtracting the configured duration. No claim involved.
export const DeviceWarrantyStatusSchema = z.enum(['active', 'expired', 'unknown']);
export const AdminDeviceWarrantySchema = z.object({
    deviceId: UuidSchema,
    name: z.string(),
    serialNumber: z.string(),
    productType: z.string().nullable(),
    ownerMobile: z.string().nullable(),
    purchaseDate: Iso8601Schema.nullable(),
    expiryDate: Iso8601Schema.nullable(),
    status: DeviceWarrantyStatusSchema,
});
export const AdminDeviceWarrantyListResponse = z.object({
    devices: z.array(AdminDeviceWarrantySchema),
    nextCursor: z.string().nullable(),
});
export const RemarkUpdateBody = z.object({
    remark: z.string().max(2000),
});
// ── POST /admin/reports ────────────────────────────────────────────────────
export const ReportTypeSchema = z.enum([
    // Devices
    'devices_by_type',
    'devices_online_offline',
    'devices_registered',
    'devices_deactivated',
    'devices_lifecycle',
    'devices_by_product',
    // Users
    'user_activity',
    'user_blocked',
    'user_by_role',
    'user_new_registrations',
    'staff_list',
    'installer_list',
    // Firmware
    'firmware_rollout',
    'firmware_versions',
    'firmware_pending',
    // Service & Warranty
    'service_requests',
    'service_open',
    'warranty_claims',
    'warranty_expiring',
    // System
    'commands_summary',
    'system_health',
    'audit_log',
]);
export const ReportFormatSchema = z.enum(['json', 'pdf', 'excel', 'csv']);
export const ReportStatusSchema = z.enum(['ready', 'generating', 'failed']);
export const ReportRequestBody = z.object({
    type: ReportTypeSchema,
    range: z.object({
        from: Iso8601Schema,
        to: Iso8601Schema,
    }),
    format: ReportFormatSchema.optional().default('json'),
    filters: z.record(z.unknown()).optional(),
});
export const ReportSubmitResponse = z.object({
    id: UuidSchema,
    status: ReportStatusSchema,
    rows: z.number().int().nonnegative().nullable(),
    generatedAt: Iso8601Schema,
    storageRef: z.string().nullable(),
});
export const GeneratedReportSchema = z.object({
    id: UuidSchema,
    type: ReportTypeSchema,
    format: ReportFormatSchema,
    rangeFrom: Iso8601Schema,
    rangeTo: Iso8601Schema,
    generatedByUserId: UuidSchema,
    generatedByMobile: E164MobileSchema,
    generatedAt: Iso8601Schema,
    status: ReportStatusSchema,
    rowCount: z.number().int().nonnegative(),
    storageRef: z.string().nullable(),
    error: z.string().nullable(),
});
export const ReportHistoryResponse = z.object({
    reports: z.array(GeneratedReportSchema),
    nextCursor: z.string().nullable(),
});
export const ReportResponse = z.object({
    type: ReportTypeSchema,
    generatedAt: Iso8601Schema,
    /** Caller-defined row shape — narrowed per report type by the consumer. */
    rows: z.array(z.record(z.unknown())),
});
// ── Remote actions (DOC1 §11 narrative — reboot, reset wifi, etc.) ─────────
export const AdminRemoteActionSchema = z.enum([
    'reboot',
    'reset_wifi',
    'activate',
    'firmware_update',
]);
export const AdminRemoteActionBody = z.object({
    action: AdminRemoteActionSchema,
    /** Optional override target firmware (only meaningful for firmware_update). */
    firmwareVersionId: UuidSchema.optional(),
});
export const AdminRemoteActionResponse = z.object({
    cmdId: z.string().min(1),
    accepted: z.literal(true),
});
// ── POST /admin/devices/{id}/reassign-owner ────────────────────────────────
//
// Staff recovery path for Main User assignment: reassigns a device's owner
// (Main User) to the given mobile — wrong number entered at assignment, owner
// unreachable, house sold, etc. Normal reassignment is the owner-driven
// transfer flow; this override is super_admin/admin only and fully audited.
export const AdminReassignOwnerBody = z.object({
    /** The mobile that should hold Main User ownership of this device. */
    mobile: E164MobileSchema,
});
export const AdminReassignOwnerResponse = z.object({
    deviceId: UuidSchema,
    ownerMobile: E164MobileSchema,
});
// ── GET /admin/system-health (T046) ──────────────────────────────────────
export const SystemHealthResponse = z.object({
    status: z.enum(['ok', 'degraded']),
    uptime: z.number().int().nonnegative(),
    db: z.object({ ok: z.boolean(), latencyMs: z.number().nonnegative() }),
    redis: z.object({ ok: z.boolean(), latencyMs: z.number().nonnegative() }),
    mqtt: z.object({ connected: z.boolean() }),
    devices: z.object({ online: z.number().int().nonnegative(), offline: z.number().int().nonnegative() }),
    commandsLast24h: z.number().int().nonnegative(),
    generatedAt: Iso8601Schema,
});
// ── GET /admin/dashboard/summary ─────────────────────────────────────────
export const DashboardAlertSchema = z.object({
    id: z.string(),
    kind: z.enum(['warranty_expiring', 'device_offline', 'service_request_stale']),
    severity: z.enum(['info', 'warning', 'critical']),
    message: z.string(),
    deviceId: UuidSchema.optional(),
    serviceRequestId: UuidSchema.optional(),
    warrantyClaimId: UuidSchema.optional(),
    ageDays: z.number().int().nonnegative().optional(),
});
export const DashboardSummaryResponse = z.object({
    devices: z.object({
        total: z.number().int().nonnegative(),
        active: z.number().int().nonnegative(),
        registered: z.number().int().nonnegative(),
        registrationPending: z.number().int().nonnegative(),
        deactivated: z.number().int().nonnegative(),
        online: z.number().int().nonnegative(),
        offline: z.number().int().nonnegative(),
    }),
    users: z.object({
        total: z.number().int().nonnegative(),
        blocked: z.number().int().nonnegative(),
    }),
    serviceRequests: z.object({
        open: z.number().int().nonnegative(),
        inReview: z.number().int().nonnegative(),
    }),
    alerts: z.array(DashboardAlertSchema),
    generatedAt: Iso8601Schema,
});
// ── GET /admin/devices  (admin-wide list) ────────────────────────────────
export const AdminProductSchema = z.enum(['m8', 'cop-lop', 'avira']);
export const AdminDeviceListQuerySchema = z.object({
    cursor: z.string().optional(),
    state: z.enum(['registered', 'pending', 'deactivated']).optional(),
    presence: z.enum(['online', 'offline']).optional(),
    search: z.string().optional(),
    product: AdminProductSchema.optional(),
    lifecycle: DeviceLifecycleStateSchema.optional(),
});
export const AdminDeviceSummarySchema = z.object({
    id: UuidSchema,
    serialNumber: z.string(),
    typeId: UuidSchema,
    typeName: z.string().nullable(),
    ownerId: UuidSchema.nullable(),
    ownerMobile: E164MobileSchema.nullable(),
    presence: z.enum(['online', 'offline']).nullable(),
    claimStatus: z.enum(['claimed', 'unclaimed']),
    sellerRegistered: z.boolean(),
    lifecycleState: DeviceLifecycleStateSchema,
    activeFirmwareVersion: z.string().nullable(),
    remark: z.string().nullable(),
    createdAt: Iso8601Schema,
    warrantyExpiresAt: Iso8601Schema.nullable().optional(),
});
export const AdminDeviceListResponse = z.object({
    devices: z.array(AdminDeviceSummarySchema),
    nextCursor: z.string().nullable(),
});
// ── GET /admin/devices/:id ───────────────────────────────────────────────
export const AdminDeviceDetailResponse = z.object({
    device: AdminDeviceSummarySchema.extend({
        name: z.string().nullable(),
        location: z.string().nullable(),
    }),
    owner: z.object({
        id: UuidSchema,
        mobile: E164MobileSchema,
        status: z.enum(['active', 'blocked']),
    }).nullable(),
    sellerRegistration: z.object({
        customerName: z.string().nullable(),
        customerMobile: z.string(),
        registeredAt: Iso8601Schema,
    }).nullable(),
    sharedUsers: z.array(z.object({
        id: UuidSchema,
        mobile: E164MobileSchema,
        permissions: z.object({
            control: z.boolean(),
            configure: z.boolean(),
            share: z.boolean(),
        }),
    })),
    warrantyClaims: z.array(WarrantyClaimSchema),
    serviceRequests: z.array(z.object({
        id: UuidSchema,
        state: z.string(),
        category: z.string(),
        description: z.string(),
        resolution: z.string().nullable(),
        createdAt: Iso8601Schema,
    })),
});
// ── GET /admin/devices/:id/logs ──────────────────────────────────────────
export const AdminDeviceLogsResponse = z.object({
    commands: z.array(z.object({
        cmdId: z.string(),
        type: z.string(),
        outcome: z.string().nullable(),
        latencyMs: z.number().nullable(),
        ts: Iso8601Schema,
    })),
    presence: z.array(z.object({
        state: z.enum(['online', 'offline']),
        ts: Iso8601Schema,
    })),
});
// ── POST /admin/production/devices  (manual add) ─────────────────────────
/**
 * Production serial: `<PREFIX>MMYY-BB-Snnn`, e.g. `HM8-0926-01-S001`.
 *   PREFIX  HM8- (Stair Light) | HCL- (Ceiling Light) | HLC- (Lift)
 *   MMYY    manufacture month + 2-digit year (Asia/Kolkata)
 *   BB      2-digit batch, 01 by default, resets every month
 *   Snnn    per-(prefix, MMYY, BB) sequence from S001
 * The per-type regex in `device_types.serial_pattern` remains the authority
 * (it also pins prefix ↔ type); this schema is the generic shape.
 */
export const PRODUCTION_SERIAL_RE = /^H(M8|CL|LC)-(0[1-9]|1[0-2])\d{2}-\d{2}-S\d{3,}$/;
export const ProductionSerialSchema = z
    .string()
    .regex(PRODUCTION_SERIAL_RE, 'expected e.g. HM8-0926-01-S001');
export const ProductionDeviceCreateBody = z.object({
    serialNumber: ProductionSerialSchema,
    secretKey: z.string().min(8).max(128),
    setupPassword: z.string().min(4).max(64),
    customerMobile: z.string().min(7).max(20).optional(),
    remark: z.string().max(2000).optional(),
});
export const ProductionDeviceCreateResponse = z.object({
    deviceIdentityId: UuidSchema,
    serialNumber: z.string(),
});
// ── GET /admin/production/next-serial ────────────────────────────────────
// Suggests the next serial for a product: batch = the one used by the most
// recent serial created this month (01 if none, or the explicit override),
// sequence = MAX within (prefix, MMYY, batch) + 1. Read-only; nothing is
// reserved — the create path's UNIQUE(serial_number) settles races.
export const NextSerialQuerySchema = z.object({
    product: AdminProductSchema,
    batch: z.coerce.number().int().min(1).max(99).optional(),
});
export const NextSerialResponse = z.object({
    product: AdminProductSchema,
    prefix: z.string(),
    period: z.string().length(4),
    batch: z.number().int().min(1).max(99),
    sequence: z.number().int().min(1),
    serialNumber: ProductionSerialSchema,
});
// ── Service requests (admin-side) ────────────────────────────────────────
export const ServiceRequestStateSchema = z.enum([
    'open',
    'in_review',
    'repair_scheduled',
    'replace_scheduled',
    'resolved',
    'rejected',
    'closed',
]);
export const ServiceRequestResolutionSchema = z.enum(['repair', 'replace', 'no_action']);
export const ServiceRequestCategorySchema = z.enum([
    'hardware',
    'installation',
    'connectivity',
    'config',
    'other',
]);
export const ServiceRequestSchema = z.object({
    id: UuidSchema,
    deviceId: UuidSchema,
    serialNumber: z.string(),
    raisedBy: z.enum(['customer', 'staff']),
    raisedByUserId: UuidSchema.nullable(),
    category: ServiceRequestCategorySchema,
    description: z.string(),
    state: ServiceRequestStateSchema,
    resolution: ServiceRequestResolutionSchema.nullable(),
    resolutionReason: z.string().nullable(),
    assignedAdminId: UuidSchema.nullable(),
    remark: z.string().nullable(),
    /** Relative API path to fetch the uploaded attachment bytes (auth required),
     *  or null when the ticket has none. Before this existed the attachment was
     *  write-only — uploaded, stored, and never readable again. */
    attachmentUrl: z.string().nullable(),
    /** MIME type of the attachment, so the client knows whether to render an
     *  image or a video player. Null when there is no attachment. */
    attachmentMimeType: z.string().nullable(),
    createdAt: Iso8601Schema,
    updatedAt: Iso8601Schema,
});
export const ServiceRequestListResponse = z.object({
    requests: z.array(ServiceRequestSchema),
    nextCursor: z.string().nullable(),
});
export const ServiceRequestCreateBody = z.object({
    serialNumber: z.string().min(1).max(64),
    category: ServiceRequestCategorySchema,
    description: z.string().min(1).max(4000),
});
export const ServiceRequestUpdateBody = z.object({
    state: ServiceRequestStateSchema.optional(),
    resolution: ServiceRequestResolutionSchema.optional(),
    resolutionReason: z.string().max(2000).optional(),
    assignedAdminId: UuidSchema.nullable().optional(),
    remark: z.string().max(2000).optional(),
});
export const ServiceRequestDetailResponse = z.object({
    request: ServiceRequestSchema,
    device: AdminDeviceSummarySchema,
    history: z.object({
        warrantyClaims: z.array(WarrantyClaimSchema),
        pastServiceRequests: z.array(ServiceRequestSchema),
    }),
});
// ── Firmware-version listing (admin view, with remark) ───────────────────
export const AdminFirmwareVersionSchema = z.object({
    id: UuidSchema,
    controllerFamily: ControllerFamilySchema,
    version: z.string(),
    isPublished: z.boolean(),
    remark: z.string().nullable(),
    createdAt: Iso8601Schema,
});
export const AdminFirmwareListResponse = z.object({
    versions: z.array(AdminFirmwareVersionSchema),
});
// ── Helper: device type alias for callers that need both enums ─────────────
export { DeviceTypeSchema, ControllerFamilySchema };
// ── Re-export lifecycle contracts for admin consumers ─────────────────────
export { DeviceLifecycleStateSchema, DeviceLifecycleHistoryResponse };
//# sourceMappingURL=admin.js.map